import React, { useEffect, useState } from 'react';
import { MainLayout } from '../components/layouts/MainLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { settingsAPI } from '../lib/firestore';
import { useToast } from '../components/ui/use-toast';
import type { Settings } from '../types';
import { Settings as SettingsIcon, Save, Plus, X, Shield, Clock, Bell } from 'lucide-react';

export const SettingsPage: React.FC = () => {
  const { toast } = useToast();
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form states
  const [remindersDelayDays, setRemindersDelayDays] = useState(7);
  const [autoRemindersEnabled, setAutoRemindersEnabled] = useState(false);
  const [allowedDomains, setAllowedDomains] = useState<string[]>([]);

  // Input fields for adding new items
  const [newDomain, setNewDomain] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await settingsAPI.get();

      if (data) {
        setSettings(data);
        setRemindersDelayDays(data.remindersDelayDays || 7);
        setAutoRemindersEnabled(data.autoRemindersEnabled ?? false);
        setAllowedDomains(data.allowedDomains || ['wifirst.fr']);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load settings',
        className: 'bg-red-50 border-red-200',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const updatedSettings: Partial<Settings> = {
        remindersDelayDays,
        autoRemindersEnabled,
        allowedDomains,
      };

      await settingsAPI.createOrUpdate(updatedSettings);

      toast({
        title: 'Success',
        description: 'Settings saved successfully',
        className: 'bg-green-50 border-green-200',
      });

      await loadSettings();
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to save settings',
        className: 'bg-red-50 border-red-200',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAddDomain = () => {
    if (newDomain && !allowedDomains.includes(newDomain)) {
      setAllowedDomains([...allowedDomains, newDomain]);
      setNewDomain('');
    }
  };

  const handleRemoveDomain = (domain: string) => {
    setAllowedDomains(allowedDomains.filter((d) => d !== domain));
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center py-12">
          <div className="text-muted-foreground">Loading settings...</div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <SettingsIcon className="h-8 w-8" />
              Global Settings
            </h1>
            <p className="text-muted-foreground mt-1">
              Configure global application settings
            </p>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>

        {/* Automatic Reminders */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Automatic Reminders
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <label className="block text-sm font-medium">
                    Enable automatic reminders
                  </label>
                  <p className="text-xs text-muted-foreground mt-1">
                    When enabled, reminder emails are sent daily at 9 AM to admins who haven't responded to offboarding requests
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={autoRemindersEnabled}
                  onClick={() => setAutoRemindersEnabled(!autoRemindersEnabled)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                    autoRemindersEnabled ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      autoRemindersEnabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {autoRemindersEnabled && (
                <div className="pt-2 border-t">
                  <label className="block text-sm font-medium mb-2">
                    Reminder Delay (days)
                  </label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Number of days after offboarding creation before sending automatic reminder emails
                  </p>
                  <input
                    type="number"
                    min="1"
                    max="30"
                    value={remindersDelayDays}
                    onChange={(e) => setRemindersDelayDays(parseInt(e.target.value) || 7)}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
                  />
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Allowed Domains */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Allowed Domains
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Only users with email addresses from these domains can access the application
              </p>

              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="example.com"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddDomain()}
                  className="flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
                />
                <Button onClick={handleAddDomain} variant="outline">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Domain
                </Button>
              </div>

              <div className="flex flex-wrap gap-2">
                {allowedDomains.map((domain) => (
                  <Badge
                    key={domain}
                    variant="outline"
                    className="flex items-center gap-2 px-3 py-1"
                  >
                    {domain}
                    <button
                      onClick={() => handleRemoveDomain(domain)}
                      className="hover:text-red-600 transition-colors"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
                {allowedDomains.length === 0 && (
                  <p className="text-sm text-muted-foreground">No domains configured</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};
